<template>
  <div class="gk-image-editor">
    <section class="gk-image-editor--img-cropper" :class="{ loading: loading }">
      <vue-cropper
        ref="cropper"
        @ready="cropperReady"
        @cropend="hasChanges = true"
        :src="imgSrc"
        :zoomOnWheel="true"
        :guides="false"
        :autoCrop="false"
        :background="false"
        :minContainerWidth="0"
        :minContainerHeight="0"
      />
      <div class="gk-image-editor-controls--toolbar">
        <div class="toolbar-group">
          <GekkoButton @click="crop" icon="fa-solid fa-crop-simple" />
        </div>
        <div class="toolbar-group">
          <GekkoButton @click="rotate(90)" icon="fa-solid fa-rotate-left" />
        </div>
      </div>
    </section>
    <section class="gk-image-editor-controls--actions">
      <div>
        <GekkoButton
          v-if="generatedImage"
          :text="$gettext('Discard')"
          variant="link text-blue"
          class="p-0"
          v-gk-confirm="{
            title: $gettext('Discard image?'),
            message: $gettext('This generated image is not saved, this action cannot be undone.'),
            options: {
              variant: 'danger',
              confirmText: $gettext('Discard')
            },
            callback: discard
          }"
        />
        <GekkoButton 
          v-else
          @click="$emit('go-back', false)"
          :text="$gettext('Cancel')"
          variant="link text-blue"
          class="p-0"
        />
      </div>
      <div class="actions-group">
        <GekkoButton 
          v-show="enabledReset"
          @click="reset"
          :disabled="!enabledReset" 
          :text="$gettext('Reset')"
          variant="light"
        />
        <GekkoButton
          @click="saveEdit"
          :disabled="!enabledSave"
          :text="$gettext('Save Image')"
        />
      </div>
    </section>
  </div>
</template>

<script>
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'

import GekkoButton from '@/components/GekkoButton'

export default {
  name: 'ImageEditor',
  components: {
    GekkoButton,
    VueCropper
  },
  props: {
    imageSrc: {
      type: String,
      default: null
    },
    generatedImage: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      imgSrc: '',
      loading: true,
      tooltipDelay: 500,
      hasChanges: false,
      originalImage: null,
    }
  },
  computed: {
    enabledSave () {
      return this.hasChanges || this.generatedImage
    },
    enabledReset () {
      return this.hasChanges
    }
  },
  watch: {
    imageSrc () {
      this.setupData()
    }
  },
  created () {
    this.setupData()
  },
  mounted () {
  },
  methods: {
    setupData () {
      if (this.imageSrc) {
        this.imgSrc = this.imageSrc
        this.originalImage = this.imageSrc
      }
    },
    cropperReady () {
      this.loading = false
    },
    reset () {
      this.$refs.cropper.reset()
      this.$refs.cropper.clear()
      this.hasChanges = false
    },
    rotate (deg) {
      this.$refs.cropper.rotate(deg);
      this.hasChanges = true
    },
    crop () {
      this.$refs.cropper.initCrop()
      this.hasChanges = true
    },
    clear () {
      this.$refs.cropper.clear()
    },
    saveEdit () {
      this.$emit('save-edit', this.$refs.cropper.getCroppedCanvas().toDataURL())
    },
    discard () {
      this.$emit('go-back', false);
    }
  }
}
</script>
<style lang="scss">
  .gk-image-editor {
    &--img-cropper {
      height: 100%;
      min-height: 200px;
      align-items: center;
      justify-content: center;
      display: flex;
      position: relative;
      > div:not(.gk-image-editor-controls--toolbar) {
        outline: 1px solid $colorBrand;
      }
      img { 
        max-height: 300px;
      }
      .cropper-container {
        .cropper-crop-box {
          .cropper-line {
            background-color: white;
            opacity: 1;
            &.line-n, &.line-s { height: 2px; }
            &.line-w, &.line-e { width: 2px; }
          }
          .cropper-point {
            width: 14px;
            height: 14px;
            border-radius: 100px;
            background-color: white;
            border: 2px solid $colorBrand;
            opacity: 1;
            &.point-n, &.point-s, &.point-e, &.point-w { display: none; }
            &.point-ne { right: -8px; top: -8px; }
            &.point-nw { left: -8px; top: -8px; }
            &.point-sw { left: -8px; bottom: -8px; }
            &.point-se { right: -8px; bottom: -8px; }
          }
        }
      }
      .gk-image-editor-controls--toolbar {
        transition: opacity 0.1s ease;
        position: absolute;
        top: 0;
        right: 0;
      }
      &:hover {
        .gk-image-editor-controls--toolbar {
          opacity: 1;
        }
      }
    }
  }
  .gk-image-editor-controls {
    &--actions {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding-top: 1rem;
      .actions-group {
        display: flex;
        gap: 0.5rem;
      }
    }
    &--toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: 2px;
      justify-content: center;
      opacity: 0;
      padding: 2px !important;
      box-shadow: rgba(64, 87, 109, 0.04) 0px 0px 0px 1px, rgba(64, 87, 109, 0.3) 0px 2px 4px -1px;
      border-radius: $radiusMd;
      background: white;
      .toolbar-group {
        display: flex;
        gap: 0rem;
      }
      .btn {
        border: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: $radiusMd;
        font-size: 1rem;
        height: 36px;
        background: white;
        color: $colorTextBase;
        &:hover, &:active, &:focus {
          background-color: #40576d12 !important;
          color: $colorTextBase !important;
        }
      }
    }
  }
</style>
<style lang="scss" scoped>
  .gk-image-editor {
    display: flex; 
    // justify-content: center; 
    align-items: center; 
    flex-direction: column;
    opacity: 1;
    transition: opacity 0.1s ease;
    height: 100%;
    justify-content: space-evenly;
    &.loading {
      opacity: 0;
    }
  }
</style>